/**
 * Design Playback Utilities
 *
 * Utility functions for design-wide playback calculations.
 * These functions help coordinate playback across multiple pages with transitions.
 */

import {
  calculateTotalDurationWithTransitions,
  getPlaybackSegmentAtTime
} from "./pageTransitionUtils";

/**
 * Get total design duration including all pages and transitions.
 * Re-exports from pageTransitionUtils for convenience.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @returns {number} Total duration in milliseconds
 */
export const getDesignTotalDuration = (pages, pagesOrder) => {
  return calculateTotalDurationWithTransitions(pages, pagesOrder);
};

/**
 * Get page-local time from design time for a specific page.
 * This converts the global design time to the time relative to a page's start.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {number} designTime - Current design-wide time in milliseconds
 * @param {string} pageId - The page ID to get local time for
 * @returns {number} Time in milliseconds relative to the page's start, or -1 if page not found
 */
export const getPageLocalTime = (pages, pagesOrder, designTime, pageId) => {
  if (!pages || !pagesOrder || pagesOrder.length === 0) {
    return -1;
  }

  const segment = getPlaybackSegmentAtTime(pages, pagesOrder, designTime);

  // If we're on the requested page, return timeInPage
  if (segment.type === "page" && segment.pageId === pageId) {
    return segment.timeInPage ?? 0;
  }

  // If we're in a transition and the requested page is the outgoing page,
  // return the end of that page's duration
  if (segment.type === "transition" && segment.pageId === pageId) {
    const page = pages[pageId];
    return page?.duration ?? 0;
  }

  // If we're in a transition and the requested page is the incoming page,
  // return 0 (transition hasn't started the page yet)
  if (segment.type === "transition" && segment.nextPageId === pageId) {
    return 0;
  }

  // Page is not currently active, return -1 to indicate this
  return -1;
};

/**
 * Calculate transition progress (0-1) from design time.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {number} designTime - Current design-wide time in milliseconds
 * @returns {number|null} Progress from 0 to 1, or null if not in a transition
 */
export const getTransitionProgress = (pages, pagesOrder, designTime) => {
  const segment = getPlaybackSegmentAtTime(pages, pagesOrder, designTime);

  if (segment.type !== "transition") {
    return null;
  }

  return segment.progress ?? 0;
};

/**
 * Check if audio should be playing at the given design time.
 * Audio should play during page segments, but NOT during transition segments.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {number} designTime - Current design-wide time in milliseconds
 * @returns {boolean} True if audio should play, false if muted (during transitions)
 */
export const shouldAudioPlayAtTime = (pages, pagesOrder, designTime) => {
  const segment = getPlaybackSegmentAtTime(pages, pagesOrder, designTime);

  // Audio plays during page segments only
  return segment.type === "page";
};

/**
 * Get the start time of a specific page in the design timeline.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {string} pageId - The page ID to get start time for
 * @returns {number} Start time in milliseconds, or -1 if page not found
 */
export const getPageStartTime = (pages, pagesOrder, pageId) => {
  if (!pages || !pagesOrder || pagesOrder.length === 0) {
    return -1;
  }

  const pageIndex = pagesOrder.indexOf(pageId);
  if (pageIndex === -1) {
    return -1;
  }

  // Calculate accumulated time up to this page
  let accumulatedTime = 0;

  for (let i = 0; i < pageIndex; i++) {
    const currentPageId = pagesOrder[i];
    const page = pages[currentPageId];
    if (!page) continue;

    // Add page duration
    accumulatedTime += page.duration || 0;

    // Add outgoing transition duration if exists
    if (page.outgoingTransition) {
      const transition = page.outgoingTransition;
      if (transition.type && transition.type !== "none") {
        accumulatedTime += transition.duration || 500;
      }
    }
  }

  return accumulatedTime;
};

/**
 * Get the design time from a page-local time for a specific page.
 * Inverse of getPageLocalTime.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {string} pageId - The page ID
 * @param {number} localTime - Time relative to page start in milliseconds
 * @returns {number} Design-wide time in milliseconds, or -1 if page not found
 */
export const getDesignTimeFromPageLocal = (
  pages,
  pagesOrder,
  pageId,
  localTime
) => {
  const pageStartTime = getPageStartTime(pages, pagesOrder, pageId);
  if (pageStartTime === -1) {
    return -1;
  }

  return pageStartTime + localTime;
};

/**
 * Get the page ID at a specific design time.
 * Returns the page that should be visible (not in transition).
 * During transitions, returns the outgoing page.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {number} designTime - Current design-wide time in milliseconds
 * @returns {string|null} Page ID or null if not found
 */
export const getPageIdAtTime = (pages, pagesOrder, designTime) => {
  const segment = getPlaybackSegmentAtTime(pages, pagesOrder, designTime);
  return segment.pageId;
};

/**
 * Check if playback is complete at the given design time.
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {number} designTime - Current design-wide time in milliseconds
 * @returns {boolean} True if playback has reached the end
 */
export const isPlaybackComplete = (pages, pagesOrder, designTime) => {
  const segment = getPlaybackSegmentAtTime(pages, pagesOrder, designTime);
  return segment.isComplete === true;
};

/**
 * Default preview duration to show of the next page after a transition (in ms)
 */
export const PAGE_PREVIEW_NEXT_PAGE_DURATION = 2000;

/**
 * Get the end time for a single page transition preview.
 * This includes: current page duration + transition duration + preview of next page.
 * If this is the last page, returns the end of that page (no transition/preview).
 *
 * @param {Object} pages - Pages object keyed by pageId
 * @param {string[]} pagesOrder - Array of page IDs in order
 * @param {string} pageId - The page ID to preview from
 * @param {number} nextPagePreviewDuration - Duration to preview of next page (default 2000ms)
 * @returns {number} End time for the preview in milliseconds, or -1 if page not found
 */
export const getPagePreviewEndTime = (
  pages,
  pagesOrder,
  pageId,
  nextPagePreviewDuration = PAGE_PREVIEW_NEXT_PAGE_DURATION
) => {
  if (!pages || !pagesOrder || pagesOrder.length === 0) {
    return -1;
  }

  const pageIndex = pagesOrder.indexOf(pageId);
  if (pageIndex === -1) {
    return -1;
  }

  const page = pages[pageId];
  if (!page) {
    return -1;
  }

  // Start with the page start time
  const pageStartTime = getPageStartTime(pages, pagesOrder, pageId);
  if (pageStartTime === -1) {
    return -1;
  }

  // Add this page's duration
  let previewEndTime = pageStartTime + (page.duration || 0);

  // Check if this is the last page
  const isLastPage = pageIndex === pagesOrder.length - 1;

  if (!isLastPage) {
    // Add transition duration if exists
    if (page.outgoingTransition) {
      const transition = page.outgoingTransition;
      if (transition.type && transition.type !== "none") {
        previewEndTime += transition.duration || 500;
      }
    }

    // Add preview of next page (clamped to next page's duration)
    const nextPageId = pagesOrder[pageIndex + 1];
    const nextPage = pages[nextPageId];
    if (nextPage) {
      const nextPageDuration = nextPage.duration || 0;
      const previewDuration = Math.min(
        nextPagePreviewDuration,
        nextPageDuration
      );
      previewEndTime += previewDuration;
    }
  }

  return previewEndTime;
};
